import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart3, Users, Bot, BookOpen, DollarSign, Settings, Menu, X, ArrowLeft, Sparkles,
} from 'lucide-react';
import MainNavigation from './MainNavigation';
import ThemeToggle from './ThemeToggle';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { label: '數據總覽', icon: BarChart3, path: '/admin/stats', roles: ['admin'] },
    {
      label: '用戶管理', icon: Users, path: '/admin/users',
      roles: ['admin', 'operator'],
      subItems: [
        { label: '用戶列表', path: '/admin/users' },
        { label: '用戶反饋', path: '/admin/feedback' },
        { label: '註冊表單配置', path: '/admin/form-config' },
        { label: '營運者操作記錄', path: '/admin/operator-logs' },
        { label: '充值訂單記錄', path: '/admin/payment-logs' },
      ],
    },
    {
      label: 'Agent 管理', icon: Bot, path: '/admin/agents',
      roles: ['admin'],
      subItems: [
        { label: 'Agent 列表', path: '/admin/agents' },
        { label: '審核 Agent', path: '/admin/review-agents' },
        { label: '審核記錄', path: '/admin/review-logs' },
      ],
    },
    {
      label: '內容管理', icon: BookOpen, path: '/admin/cases',
      roles: ['admin', 'operator'],
      subItems: [
        { label: '案例庫', path: '/admin/cases' },
        { label: '分類管理', path: '/admin/case-categories' },
        { label: '用戶內容記錄', path: '/admin/user-videos' },
        { label: '案例分析設定', path: '/admin/case-analysis-settings' },
        { label: '影片診斷設定', path: '/admin/scorecard-config' },
        { label: '輪播 Prompt 管理', path: '/admin/carousel-prompts' },
      ],
    },
    { label: '計費管理', icon: DollarSign, path: '/admin/credit-plans', roles: ['admin'],
      subItems: [
        { label: '點數方案', path: '/admin/credit-plans' },
        { label: '計費設定', path: '/admin/billing' },
      ],
    },
    {
      label: '系統設定', icon: Settings, path: '/admin/settings',
      roles: ['admin'],
      subItems: [
        { label: '基本設定', path: '/admin/settings' },
        { label: '表單配置', path: '/admin/form-config' },
        { label: '系統診斷', path: '/admin/diagnostics' },
      ],
    },
  ];

  const filtered = menuItems.filter((i) => i.roles.includes(user?.role));
  const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + '/');
  const toTestId = (path, prefix) => `${prefix}-${path.replace(/\//g, '-').replace(/^-+/, '')}`;
  const isGroupActive = (item) =>
    item.subItems ? item.subItems.some((s) => isActive(s.path)) : isActive(item.path);

  const handleNav = (p) => { navigate(p); setMobileMenuOpen(false); };

  const SidebarContent = () => (
    <nav className="flex-1 overflow-y-auto sidebar-scroll p-2 space-y-0.5">
      {filtered.map((item) => {
        const Icon = item.icon;
        const active = isGroupActive(item);
        return (
          <div key={item.path}>
            <button
              onClick={() => handleNav(item.path)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-all duration-200 relative ${
                active
                  ? 'bg-white/[0.06] text-white'
                  : 'text-obsidian-200 hover:bg-white/[0.03] hover:text-white'
              }`}
              data-testid={toTestId(item.path, 'admin-nav')}
            >
              {active && (
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-lime-500 rounded-r"
                  style={{ boxShadow: '0 0 6px rgba(255,84,54,0.6)' }}
                />
              )}
              <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={active ? 2 : 1.5} />
              <span>{item.label}</span>
            </button>

            {item.subItems && active && (
              <div className="ml-7 mt-0.5 mb-1 space-y-0.5">
                {item.subItems.map((sub) => {
                  const subActive = isActive(sub.path);
                  return (
                    <button
                      key={sub.path}
                      onClick={() => handleNav(sub.path)}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-[11px] transition-all duration-200 ${
                        subActive
                          ? 'text-lime-500 font-medium bg-lime-500/[0.08]'
                          : 'text-obsidian-300 hover:text-obsidian-100'
                      }`}
                      data-testid={toTestId(sub.path, 'admin-subnav')}
                    >
                      {sub.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div
      data-theme="dark"
      data-atelier="true"
      className="flex h-screen surface-obsidian text-white admin-dark-scope"
    >
      <MainNavigation />

      {/* Desktop Admin Sidebar */}
      <aside className="hidden md:flex w-56 bg-obsidian-950 border-r border-obsidian-600 flex-col flex-shrink-0">
        <div className="px-4 pt-6 pb-4 border-b border-obsidian-600">
          <div className="flex items-center gap-2.5 mb-3">
            <div
              className="w-7 h-7 rounded-md bg-lime-500 flex items-center justify-center"
              style={{ boxShadow: '0 0 12px rgba(255,84,54,0.4)' }}
            >
              <Sparkles className="w-3.5 h-3.5 text-obsidian-900" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold text-white text-sm tracking-tight">
                管理後台
              </div>
              <div className="label-micro text-lime-500">CONTROL · ROOM</div>
            </div>
            <ThemeToggle />
          </div>
        </div>
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 surface-glass-dark border-b border-obsidian-600 flex items-center h-12 px-4">
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-1.5 rounded-md hover:bg-white/5 text-obsidian-200 mr-3"
          data-testid="admin-mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h2 className="text-sm font-semibold text-white truncate">
          管理後台 <span className="label-micro text-lime-500 ml-2">CONTROL</span>
        </h2>
        <button
          onClick={() => navigate('/create')}
          className="ml-auto p-1.5 rounded-md hover:bg-white/5 text-obsidian-300"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-obsidian-950 border-r border-obsidian-600 flex flex-col animate-slide-in-left">
            <div className="p-4 border-b border-obsidian-600 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-md bg-lime-500 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-obsidian-900" strokeWidth={2.5} />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">管理後台</h2>
                  <p className="label-micro text-lime-500 mt-0.5">CONTROL · ROOM</p>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1.5 rounded-md hover:bg-white/5 text-obsidian-300">
                <X className="w-4 h-4" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden pt-12 md:pt-0 pb-14 md:pb-0 bg-obsidian-900 relative">
        {/* Top brand neon strip */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-lime-500/50 to-transparent z-10 pointer-events-none" />
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
